import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button, Loading } from "@carbon/react";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import FileReader from "../../components/FileReader/FileReader";
import IsMaterialOffer from "../../components/Offer/IsMaterialOffer";
import { useID } from "../../hooks";
import {
  IT_DOESNT_HAVE_SPEC,
  TO_BE_AGREED,
  IS_CONFIDENTIAL,
  addressFormatter,
} from "../../utils";
import { useSeller } from "../../hooks/useValidateSeller";
import { useMaterialOffer } from "../../hooks/useMaterialsOffers";
import "./materialOffer.scss";

/**
 * Seller-side material-offer (application) detail page, rebuilt on the Carbon primitives layer
 * (sub-flow C, Task 4). Consumes the T2 lean `MaterialOfferResponse`/`MaterialOfferMaterialResponse`
 * DTO (`types/materialOffer.type.ts`) — no field read here beyond what that type declares.
 *
 * `GoBackArrow` (a bespoke icon button) is replaced with a plain Carbon ghost `Button`, same swap
 * `User.tsx` already made for its own back link — `GoBackArrow`'s `isMaterialOffer` branch always
 * fired on this exact route (`location.pathname.includes("/material/offer")`), so the destination
 * (`/material/offers`) is hardcoded here rather than carried over as conditional logic.
 *
 * `IsMaterialOffer` (`components/Offer/IsMaterialOffer.tsx`, imported here under its real name —
 * the pre-Carbon file aliased it to `IsSellerOffer` for no evident reason) is OUT of this task's
 * scope (T7 migrates the remaining offer-display components) and is rendered with its contract
 * exactly unchanged (`materialOffer`/`valid` props). It still renders its own Tailwind
 * `col-span-2 lg:col-span-1` markup; `.material-offer__layout` below is a real CSS grid with the
 * same unconditional 2-column template the pre-Carbon `grid grid-cols-2` Tailwind class had, so
 * `IsMaterialOffer`'s own span classes keep positioning it correctly without needing to touch it.
 */
const MaterialOffer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useID();

  const { data: seller } = useSeller();

  const {
    data: materialOffer,
    isLoading: isLoadingMaterialOffer,
    isError,
  } = useMaterialOffer(id);

  const sellerFound = seller?.find(
    (sel) => sel.material.id === materialOffer?.material?.id
  );

  if (isError) navigate("/not-found");

  if (isLoadingMaterialOffer) {
    return <Loading withOverlay={false} description={t("cta.loading")} small />;
  }

  return (
    <div>
      <Button
        as={Link}
        to="/material/offers"
        kind="ghost"
        size="sm"
        className="material-offer__back"
      >
        {t("cta.goBack")}
      </Button>

      <PageHeader title={materialOffer?.material?.globalMaterialName} />

      <div className="material-offer__layout">
        <div className="material-offer__panel cds-form-surface">
          <dl className="material-offer__details">
            {sellerFound?.validated ? (
              <>
                <div className="material-offer__detail-row">
                  <dt>{t("tenders.form.company")}</dt>
                  <dd>{materialOffer?.material?.company?.name}</dd>
                </div>
                <div className="material-offer__detail-row">
                  <dt>{t("tenders.form.fullName")}</dt>
                  <dd>{materialOffer?.material?.owner?.fullName}</dd>
                </div>
                <div className="material-offer__detail-row">
                  <dt>{t("tenders.form.email")}</dt>
                  <dd>{materialOffer?.material?.owner?.email}</dd>
                </div>
                <div className="material-offer__detail-row">
                  <dt>{t("tenders.form.phone")}</dt>
                  <dd>{materialOffer?.material?.owner?.phone}</dd>
                </div>
              </>
            ) : null}
            {materialOffer?.material?.certifications &&
            materialOffer.material.certifications.length > 0 ? (
              <div className="material-offer__detail-row">
                <dt>{t("certifications.certifications")}</dt>
                <dd>
                  {materialOffer.material.certifications
                    .map((cert) => cert.name)
                    .join(", ")}
                </dd>
              </div>
            ) : null}
            {materialOffer?.material?.deliveryFormat ? (
              <div className="material-offer__detail-row">
                <dt>{t("newMaterialForm.deliveryFormat")}</dt>
                <dd>{materialOffer.material.deliveryFormat}</dd>
              </div>
            ) : null}
            <div className="material-offer__detail-row">
              <dt>{t("newMaterialForm.deliveryPlace")}</dt>
              <dd>
                {addressFormatter(
                  materialOffer?.material?.deliveryPlace?.address as string
                ) ?? TO_BE_AGREED}
              </dd>
            </div>
            {materialOffer?.material?.comments ? (
              <div className="material-offer__detail-row">
                <dt>{t("newMaterialForm.comments")}</dt>
                <dd>{materialOffer.material.comments}</dd>
              </div>
            ) : null}
            {materialOffer?.material?.brand ? (
              <div className="material-offer__detail-row">
                <dt>{t("newMaterialForm.brand")}</dt>
                <dd>{materialOffer.material.brand}</dd>
              </div>
            ) : null}
            <div className="material-offer__detail-row">
              <dt>{t("newMaterialForm.specification")}</dt>
              <dd>
                <Fragment>
                  {materialOffer?.material?.confidentSpec &&
                  !sellerFound?.validated ? (
                    <p>{IS_CONFIDENTIAL}</p>
                  ) : materialOffer?.material?.fileURL ? (
                    <FileReader fileKey={materialOffer.material.fileURL} />
                  ) : (
                    <p>{IT_DOESNT_HAVE_SPEC}</p>
                  )}
                </Fragment>
              </dd>
            </div>
          </dl>
        </div>

        {materialOffer ? (
          <IsMaterialOffer
            materialOffer={materialOffer}
            valid={sellerFound?.validated}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MaterialOffer;
